import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../lib/auth';
import { authAPI } from '../../lib/api';
import { Wrench, Loader2, AlertCircle, Lock, Mail, User } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [needsSetup, setNeedsSetup] = useState(false);
  const [checkingSetup, setCheckingSetup] = useState(true);
  const [setupName, setSetupName] = useState('');
  const [setupEmail, setSetupEmail] = useState('');
  const [setupPassword, setSetupPassword] = useState('');
  const [setupConfirm, setSetupConfirm] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    authAPI.checkSetup()
      .then(r => setNeedsSetup(r.data.needs_setup))
      .catch(() => {})
      .finally(() => setCheckingSetup(false));
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate('/admin');
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (detail === 'NEEDS_SETUP') {
        setNeedsSetup(true);
      } else if (status === 429) {
        setError('Cok fazla deneme. Lutfen bekleyin.');
      } else if (status === 401) {
        setError('E-posta veya sifre hatali.');
      } else {
        setError(detail || 'Baglanti hatasi.');
      }
    } finally { setLoading(false); }
  };

  const handleSetup = async (e) => {
    e.preventDefault();
    setError('');
    if (!setupPassword || setupPassword.length < 6) { setError('Sifre en az 6 karakter olmali.'); return; }
    if (setupPassword !== setupConfirm) { setError('Sifreler eslesmiyor.'); return; }
    setLoading(true);
    try {
      const res = await authAPI.setup({
        email: setupEmail || 'admin@uretimnoktasi.com',
        password: setupPassword,
        name: setupName || 'Admin'
      });
      localStorage.setItem('token', res.data.token);
      localStorage.setItem('user', JSON.stringify(res.data.user));
      window.location.href = '/admin';
    } catch (err) {
      setError(err.response?.data?.detail || 'Kurulum basarisiz.');
    } finally { setLoading(false); }
  };

  if (checkingSetup) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-8 w-8 animate-spin text-[hsl(221,83%,22%)]" />
      </div>
    );
  }

  // SETUP MODE - First time, set password
  if (needsSetup) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Wrench className="h-8 w-8 text-[hsl(221,83%,22%)]" />
            </div>
            <CardTitle className="text-xl">Ilk Kurulum</CardTitle>
            <p className="text-sm text-muted-foreground mt-2">Admin hesabiniz icin e-posta ve sifre belirleyin.</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSetup} className="space-y-4" data-testid="setup-form">
              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-50 text-red-800 rounded-lg text-sm" role="alert">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  {error}
                </div>
              )}
              <div>
                <Label htmlFor="setup-name"><User className="h-3.5 w-3.5 inline mr-1" />Ad Soyad</Label>
                <Input id="setup-name" value={setupName} onChange={e => setSetupName(e.target.value)} placeholder="Adiniz" data-testid="setup-name" />
              </div>
              <div>
                <Label htmlFor="setup-email"><Mail className="h-3.5 w-3.5 inline mr-1" />E-posta</Label>
                <Input id="setup-email" type="email" value={setupEmail} onChange={e => setSetupEmail(e.target.value)} placeholder="admin@uretimnoktasi.com" data-testid="setup-email" />
              </div>
              <div>
                <Label htmlFor="setup-pw"><Lock className="h-3.5 w-3.5 inline mr-1" />Sifre</Label>
                <Input id="setup-pw" type="password" value={setupPassword} onChange={e => setSetupPassword(e.target.value)} placeholder="En az 6 karakter" required data-testid="setup-password" />
              </div>
              <div>
                <Label htmlFor="setup-confirm"><Lock className="h-3.5 w-3.5 inline mr-1" />Sifre Tekrar</Label>
                <Input id="setup-confirm" type="password" value={setupConfirm} onChange={e => setSetupConfirm(e.target.value)} placeholder="Sifreyi tekrar girin" required data-testid="setup-confirm" />
              </div>
              <Button type="submit" className="w-full" disabled={loading} data-testid="setup-submit">
                {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Kayit ediliyor...</> : 'Hesabi Olustur'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // NORMAL LOGIN
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Wrench className="h-8 w-8 text-[hsl(221,83%,22%)]" />
          </div>
          <CardTitle className="text-xl">Admin Girisi</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4" data-testid="login-form">
            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-50 text-red-800 rounded-lg text-sm" role="alert" data-testid="login-error">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                {error}
              </div>
            )}
            <div>
              <Label htmlFor="email">E-posta</Label>
              <Input id="email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="admin@uretimnoktasi.com" required data-testid="login-email" />
            </div>
            <div>
              <Label htmlFor="password">Sifre</Label>
              <Input id="password" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Sifreniz" required data-testid="login-password" />
            </div>
            <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit">
              {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Giris yapiliyor...</> : 'Giris Yap'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
